import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Alert, Spin, Card, Typography, Tabs, Space, Row, Col, Tag, Divider, Segmented } from 'antd';
import MiniChart from '../Chart/MiniChart';
import { RobotOutlined, EyeOutlined, DotChartOutlined, SaveOutlined, SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import Dashboard from './Dashboard';
import HeatmapTable from './HeatmapTable';
import AverageReturnTable from './AverageReturnTable';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useUsageTracking } from '../../hooks/useUsageTracking';
import { logger } from '../../utils/logger';

const { Text } = Typography;

interface PointAnalysisProps {
  ohlcData: any[];
  selectedPoint?: { time: number; price: number };
  onSaveSignal?: (analysisResult: any, context: any) => void;
  // Agent can trigger the same behavior as clicking "Signal Point Analysis" button
  autoRunToken?: number;
  // When triggered by agent, avoid double usage counting (AgentChat already checked usage)
  autoRunSkipUsageCheck?: boolean;
}

function formatTimestamp(ts: string | number | undefined | null): string {
  if (!ts) return "未知";
  let date: Date;
  if (typeof ts === "string") {
    date = new Date(ts);
    if (isNaN(date.getTime())) {
      const num = parseInt(ts);
      if (!isNaN(num)) {
        date = num > 1e12 ? new Date(num) : new Date(num * 1000);
      }
    }
  } else {
    date = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
  }
  if (!date || isNaN(date.getTime())) return "未知";
  return date.toISOString().replace('T', ' ').slice(0, 19);
}

// 将任意时间表示转换为秒级 Unix 时间
function toUnixSeconds(ts: string | number): number {
  if (typeof ts === 'number') {
    return ts > 1e12 ? Math.floor(ts / 1000) : ts;
  }
  const n = Number(ts);
  if (!Number.isNaN(n)) {
    return n > 1e12 ? Math.floor(n / 1000) : n;
  }
  const d = new Date(ts);
  const ms = d.getTime();
  return Number.isNaN(ms) ? 0 : Math.floor(ms / 1000);
}

function calcFutureReturn(ohlcData: any[], idx: number, period: number) {
  if (idx + period >= ohlcData.length) return null;
  const now = ohlcData[idx].close;
  const future = ohlcData[idx + period].close;
  return (future - now) / now;
}

// Subsequent Return Analysis Tab with Discovery/Validation toggle
interface SubsequentReturnAnalysisTabProps {
  pointReturnsAnalysis: any;
  discoveryData: any;
  validationData: any;
  theme: any;
  t: any;
}

const SubsequentReturnAnalysisTab: React.FC<SubsequentReturnAnalysisTabProps> = ({
  pointReturnsAnalysis,
  discoveryData,
  validationData,
  theme,
  t
}) => {
  const [viewMode, setViewMode] = useState<'all' | 'discovery' | 'validation'>('all');
  
  const hasDiscovery = discoveryData?.returns_analysis && discoveryData.sample_count > 0;
  const hasValidation = validationData?.returns_analysis && validationData.sample_count > 0;
  
  // 获取当前要显示的数据
  const getCurrentData = () => {
    if (viewMode === 'discovery' && hasDiscovery) {
      return { returns_analysis: discoveryData.returns_analysis };
    }
    if (viewMode === 'validation' && hasValidation) {
      return { returns_analysis: validationData.returns_analysis };
    }
    return pointReturnsAnalysis;
  };
  
  const currentData = getCurrentData();
  
  // 获取样本数描述
  const getSampleInfo = () => {
    if (viewMode === 'discovery') {
      return `${discoveryData?.sample_count || 0} ${t('analysis.split.samples', 'samples')}`;
    }
    if (viewMode === 'validation') {
      return `${validationData?.sample_count || 0} ${t('analysis.split.samples', 'samples')}`;
    }
    return null;
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Mode Selector */}
      {(hasDiscovery || hasValidation) && (
        <div style={{ 
          marginBottom: 20, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 16,
          flexWrap: 'wrap'
        }}>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as 'all' | 'discovery' | 'validation')}
            options={[
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>📊</span>
                    <span>{t('analysis.split.all', 'All Data')}</span>
                  </div>
                ),
                value: 'all',
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SearchOutlined style={{ color: '#1890ff' }} />
                    <span style={{ color: hasDiscovery ? undefined : '#999' }}>
                      {t('analysis.split.discovery', 'Discovery')}
                    </span>
                    {hasDiscovery && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                        {discoveryData.sample_count}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'discovery',
                disabled: !hasDiscovery,
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span style={{ color: hasValidation ? undefined : '#999' }}>
                      {t('analysis.split.validation', 'Validation')}
                    </span>
                    {hasValidation && (
                      <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                        {validationData.sample_count}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'validation',
                disabled: !hasValidation,
              },
            ]}
            style={{ background: theme.toolbarBackground }}
          />
        </div>
      )}
      
      {/* Analysis Content */}
      {currentData ? (
        <>
          <Dashboard returnsAnalysis={currentData} />
          <Divider />
          <HeatmapTable returnsAnalysis={currentData} />
          <Divider />
          <AverageReturnTable returnsAnalysis={currentData} />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.textSecondary }}>
          <div>{t('analysis.noData', 'No data')}</div>
        </div>
      )}
    </div>
  );
};

// Similar Point List with Discovery/Validation filter
interface SimilarPointListProps {
  filteredPoints: any[];
  targetIndex: number;
  displayCount: number;
  theme: any;
  t: any;
  onViewPoint: (pt: any) => void;
  formatTimestamp: (ts: string | number) => string;
}

const SimilarPointList: React.FC<SimilarPointListProps> = ({
  filteredPoints,
  targetIndex,
  displayCount,
  theme,
  t,
  onViewPoint,
  formatTimestamp
}) => {
  const [viewMode, setViewMode] = useState<'all' | 'discovery' | 'validation'>('all');
  
  // 分割数据 - 基于 index 字段
  const discoveryPoints = filteredPoints.filter((pt: any) => (pt.index ?? 0) < targetIndex);
  const validationPoints = filteredPoints.filter((pt: any) => (pt.index ?? 0) >= targetIndex);
  
  const hasDiscovery = discoveryPoints.length > 0;
  const hasValidation = validationPoints.length > 0;
  
  // 获取当前要显示的数据
  const getCurrentPoints = () => {
    if (viewMode === 'discovery') return discoveryPoints;
    if (viewMode === 'validation') return validationPoints;
    return filteredPoints;
  };
  
  const currentPoints = getCurrentPoints();

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Filter Selector */}
      {(hasDiscovery || hasValidation) && (
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center'
        }}>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as 'all' | 'discovery' | 'validation')}
            options={[
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>📊</span>
                    <span>{t('analysis.split.all', 'All')}</span>
                    <Tag style={{ margin: 0, fontSize: 10 }}>{filteredPoints.length}</Tag>
                  </div>
                ),
                value: 'all',
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <SearchOutlined style={{ color: '#1890ff' }} />
                    <span style={{ color: hasDiscovery ? undefined : '#999' }}>
                      {t('analysis.split.discovery', 'Discovery')}
                    </span>
                    {hasDiscovery && (
                      <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>
                        {discoveryPoints.length}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'discovery',
                disabled: !hasDiscovery,
              },
              {
                label: (
                  <div style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span style={{ color: hasValidation ? undefined : '#999' }}>
                      {t('analysis.split.validation', 'Validation')}
                    </span>
                    {hasValidation && (
                      <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                        {validationPoints.length}
                      </Tag>
                    )}
                  </div>
                ),
                value: 'validation',
                disabled: !hasValidation,
              },
            ]}
            style={{ background: theme.toolbarBackground }}
          />
        </div>
      )}

      {/* Points List */}
      <div style={{ maxHeight: 320, overflowY: 'auto', background: theme.toolbarBackground, borderRadius: 8, padding: 8 }}>
        {currentPoints.length > 0 ? (
          currentPoints.slice(0, displayCount).map((pt: any, idx: number) => (
            <Card
              key={idx}
              size="small"
              style={{
                marginBottom: 8,
                border: `1px solid ${theme.surfaceBorder}`,
                borderRadius: 6,
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              hoverable
              styles={{ body: { padding: '12px' } }}
            >
              <Row align="middle" justify="space-between">
                <Col flex="auto">
                  <div style={{ marginBottom: 4 }}>
                    <Text strong style={{ fontSize: '13px' }}>
                      {formatTimestamp(pt.datetime)}
                    </Text>
                  </div>
                  <div>
                    <Tag color="blue" style={{ marginRight: 8 }}>
                      {t('analysis.correlationCoefficient')}: {(pt.similarity ?? (1 - pt.distance)).toFixed(3)}
                    </Tag>
                  </div>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => onViewPoint(pt)}
                  >
                    {t('analysis.view')}
                  </Button>
                </Col>
              </Row>
            </Card>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: theme.textSecondary }}>
            <div>{t('analysis.noData')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const PointAnalysis: React.FC<PointAnalysisProps> = ({ ohlcData, selectedPoint, onSaveSignal, autoRunToken, autoRunSkipUsageCheck }) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [activeMiniChartPoint, setActiveMiniChartPoint] = useState<{ time: number; price: number } | null>(null);
  const [displayCount] = useState<number>(20);
  const [activeInnerTab, setActiveInnerTab] = useState<string>('1');
  const [targetIndex, setTargetIndex] = useState<number>(0);
  
  // 使用次数跟踪
  const { canUse, checkAndRecordUsage, showUsageExceededMessage } = useUsageTracking();

  // 预计算时间数组（秒），便于二分查找，避免每次点击 O(N) 扫描导致卡顿
  const timeSeconds = useMemo(() => {
    return ohlcData.map((d: any) => Number(d.time));
  }, [ohlcData]);

  // 在有序时间序列上二分查找，返回最接近 targetSec 的索引
  function findNearestIndexByTime(targetSec: number): number {
    if (!timeSeconds.length) return -1;
    let left = 0, right = timeSeconds.length - 1;
    // 若越界，直接返回端点
    if (targetSec <= timeSeconds[left]) return left;
    if (targetSec >= timeSeconds[right]) return right;
    while (left <= right) {
      const mid = (left + right) >> 1;
      const t = timeSeconds[mid];
      if (t === targetSec) return mid;
      if (t < targetSec) left = mid + 1; else right = mid - 1;
    }
    // left 是第一个大于 target 的位置，比较 left 与 left-1 哪个更近
    const cand1 = Math.max(0, left - 1);
    const cand2 = Math.min(timeSeconds.length - 1, left);
    return Math.abs(timeSeconds[cand1] - targetSec) <= Math.abs(timeSeconds[cand2] - targetSec) ? cand1 : cand2;
  }

  // 移除未使用的本地向量数据加载，减少无效内存与渲染负担

  const analyzePoint = async (opts?: { skipUsageCheck?: boolean }) => {
    if (!selectedPoint || selectedPoint.time === undefined) return;
    
    // 立即显示 loading 状态，避免用户觉得系统没响应
    setLoading(true);
    setError(null);
    setResult(null);
    
    // 检查使用次数限制（agent auto-run 可跳过，避免重复计数）
    if (!opts?.skipUsageCheck) {
    const canProceed = await checkAndRecordUsage();
    if (!canProceed) {
      setLoading(false);
      return;
      }
    }
    // 选择离所选时间最近的bar索引（O(log N) 二分）
    const idx = findNearestIndexByTime(Number(selectedPoint.time));
    setTargetIndex(idx);
    try {
      // 可选：截一张过去20根K线的图（未来可接入MultiPaneChart的ref，这里先留空）
      let chartImage: string | undefined = undefined;
      const payload = {
        ohlc_data: ohlcData,
        target_index: idx,
        top_k: 200,
        prior_only: false, 
        include_ai: true,
        forward_periods: [10, 20, 50, 100],
        language: i18n.language || (window as any).APP_CONFIG?.LANG || (navigator.language || 'zh-CN'),
        ...(chartImage ? { chart_image: chartImage } : {})
        // 可传 weights 自定义权重
        // weights: { 'DVR_Value': 1.2, 'RSI_14': 1.1 }
      } as any;
      const { getConfigAsync } = await import('../../utils/config');
      const { getApiHeaders } = await import('../../utils/apiHeaders');
      const config = await getConfigAsync();
      const API_BASE_URL = config.API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/point_similarity/knn_similarity`, {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("后端请求失败");
      const data = await response.json();
      setResult(data);
      if (data?.timing_ms) {
        logger.log('KNN timing (ms):', data.timing_ms);
      }
      setActiveMiniChartPoint(null); // 每次分析后重置为默认信号点
    } catch (err: any) {
      setError(err.message || "请求失败");
    } finally {
      setLoading(false); 
    }
  };

  // Agent-triggered auto-run: mimic clicking the analysis button
  const lastAutoRunTokenRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (autoRunToken === undefined) return;
    if (lastAutoRunTokenRef.current === autoRunToken) return;
    lastAutoRunTokenRef.current = autoRunToken;
    if (!selectedPoint || selectedPoint.time === undefined) return;
    if (loading) return;
    analyzePoint({ skipUsageCheck: !!autoRunSkipUsageCheck });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunToken]);

  const handleSaveSignal = () => {
    if (!result) return;
    if (!selectedPoint) return;
    if (!onSaveSignal) return;

    // 将点分析结果转换为与矩形保存兼容的结构
    // 重要：必须包含 similar_points 以便后端提取相似模式用于回测
    // 合并后端返回的 discovery/validation 数据用于 Before/After 分析
    const backendReturns = result?.returns_analysis || {};
    const mergedReturnsAnalysis = {
      ...(pointReturnsAnalysis?.returns_analysis || {}),
      discovery: backendReturns.discovery,
      validation: backendReturns.validation
    };
    
    const analysisResult = {
      similarity_analysis: {
        // 点分析没有区间相似度，保留空结构，便于后端prepare_analysis_data的兼容性检查
      },
      similar_points: result.similar_points || [],  // 关键：传递相似点数据
      target_point: result.target_point || null,    // 传递目标点数据
      // CRITICAL: pass through scaler params so backend can store them in DynamoDB.
      // This allows realtime similarity checks to be bit-for-bit consistent with the original analysis.
      scaler_params: (result as any).scaler_params || null,
      returns_analysis: mergedReturnsAnalysis
    };

    // 保持与矩形工具一致，直接传递 Unix 时间戳（秒）
    // selectedPoint.time 可能是秒或毫秒，统一转为秒
    const pointTimeSec = typeof selectedPoint.time === 'number' && selectedPoint.time < 1e12
      ? selectedPoint.time
      : Math.floor((selectedPoint.time as number) / 1000);

    const context = {
      // 直接传递时间戳，点等价为同一时刻（与矩形工具格式一致）
      timeRange: {
        start_time: pointTimeSec,
        end_time: pointTimeSec
      },
      // 也把原始点信息传给上层以便需要时使用
      selectedPoint
    };

    onSaveSignal(analysisResult, context);
  };

  // 相似点过滤和相关系数
  const filteredPoints = useMemo(() => {
    return result?.similar_points?.filter((pt: any) => (pt.similarity ?? (1 - pt.distance)) >= 0.8) || [];
  }, [result]);

  // 后续收益分析
  const periods = [10, 30, 50];
  // 预构建时间到索引的映射，避免重复 O(N) 查找
  const timeToIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    for (let i = 0; i < timeSeconds.length; i++) map.set(Number(timeSeconds[i]), i);
    return map;
  }, [timeSeconds]);

  const avgReturns = useMemo(() => {
    if (activeInnerTab !== '2' || !filteredPoints.length) return periods.map(() => null);
    return periods.map(period => {
      const returns = filteredPoints
        .map((pt: any) => {
          const tSec = toUnixSeconds(pt.datetime);
          const idx = timeToIndexMap.get(tSec) ?? findNearestIndexByTime(tSec);
          return calcFutureReturn(ohlcData, idx, period);
        })
        .filter((r: number | null) => r !== null) as number[];
      if (returns.length === 0) return null;
      return (returns.reduce((a, b) => a + b, 0) / returns.length) * 100;
    });
  }, [filteredPoints, ohlcData, activeInnerTab, timeToIndexMap]);

  // 组装 returns_analysis 结构，复用矩形分析的组件
  const pointReturnsAnalysis = useMemo(() => {
    if (!result || !filteredPoints.length) return null;
    // 按相似度排序（降序）
    const sorted = [...filteredPoints].sort((a: any, b: any) => (b.similarity ?? (1-b.distance)) - (a.similarity ?? (1-a.distance)));
    const rankGroups = [3, 10, 20, 50, 100];
    const forwardPeriods = [10, 20, 50, 100];

    const computeForwardReturn = (idx: number, p: number): number | null => {
      if (idx < 0) return null;
      const r = calcFutureReturn(ohlcData, idx, p);
      if (r === null) return null;
      return r * 100; // 转为百分比
    };

    const returns_analysis: any = {};
    forwardPeriods.forEach((p) => {
      const key = `forward_${p}`;
      returns_analysis[key] = {};
      rankGroups.forEach((rg) => {
        const takeN = Math.min(rg, sorted.length);
        if (takeN <= 0) return;
        const subset = sorted.slice(0, takeN);
        const returns: number[] = [];
        for (const pt of subset) {
          const tSec = toUnixSeconds(pt.datetime);
          const idx = timeToIndexMap.get(tSec) ?? findNearestIndexByTime(tSec);
          const ret = computeForwardReturn(idx, p);
          if (ret !== null) returns.push(ret);
        }
        if (returns.length === 0) {
          returns_analysis[key][`top_${rg}`] = { mean_return: 0, volatility: 0, win_rate: 0, sample_count: 0 };
        } else {
          const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
          const variance = returns.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / returns.length;
          const std = Math.sqrt(variance);
          const winRate = (returns.filter(v => v > 0).length / returns.length) * 100;
          returns_analysis[key][`top_${rg}`] = {
            mean_return: +mean.toFixed(2),
            volatility: +std.toFixed(2),
            win_rate: +winRate.toFixed(1),
            sample_count: returns.length,
          };
        }
      });
    });

    return { returns_analysis };
  }, [result, filteredPoints, ohlcData, timeToIndexMap]);

  // MiniChart 数据逻辑
  const miniChartPoint = activeMiniChartPoint || selectedPoint;
  const { miniChartData, miniChartTitle, highlightRange } = useMemo(() => {
    let data: any[] = [];
    let title = '';
    let range: { from: string; to: string } | undefined = undefined;
    if (miniChartPoint && ohlcData.length > 0) {
      const centerIdx = findNearestIndexByTime(Number(miniChartPoint.time));
      if (centerIdx >= 0) {
        const halfWindow = 20; // 前后各20根
        const fromIdx = Math.max(0, centerIdx - halfWindow);
        const toIdx = Math.min(ohlcData.length - 1, centerIdx + halfWindow);
        data = ohlcData.slice(fromIdx, toIdx + 1).map(d => ({
          ...d,
          datetime: d.datetime || (typeof d.time === 'number' ? new Date(d.time * 1000).toISOString() : ''),
          time: Number(d.time)
        }));
        title = `${t('analysis.miniKlines')}（${t('analysis.center')}: ${formatTimestamp(ohlcData[centerIdx].time)}）`;
        range = { from: ohlcData[centerIdx].time, to: ohlcData[centerIdx].time } as any;
      }
    }
    return { miniChartData: data, miniChartTitle: title, highlightRange: range };
  }, [miniChartPoint, ohlcData, t]);

  return (
    <Card
      title={
        <Space>
          <DotChartOutlined style={{ color: '#1890ff' }} />
          <span>{t('analysis.signalPointAnalysis')}</span>
        </Space>
      }
      style={{
        width: '100%',
        margin: 0,
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}
      styles={{
        body: { padding: 24 },
        header: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 选择点信息 + 开始分析 */}
        <Card size="small" style={{ background: theme.toolbarBackground, borderRadius: 8 }}>
          <Row align="middle" justify="space-between">
            <Col>
              <Text strong>{t('analysis.currentSelectedPoint')}: </Text>
              {selectedPoint && selectedPoint.time !== undefined ? (
                <Tag color="blue" style={{ fontSize: '13px' }}>
                  {formatTimestamp(selectedPoint.time)}
                </Tag>
              ) : (
                <Tag color="default">{t('analysis.noPointSelected')}</Tag>
              )}
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={() => analyzePoint()}
                loading={loading}
                disabled={!selectedPoint || !canUse}
                size="large"
                style={{
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}
              >
                {t('analysis.signalPointAnalysisButton')}
              </Button>
            </Col>
          </Row>
        </Card>

        {error && (
          <Alert
            message="分析错误"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ borderRadius: 8 }}
          />
        )}

        {loading && (
          <Card style={{ textAlign: 'center', padding: '40px', background: theme.toolbarBackground }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: '16px', color: '#666' }}>
              <RobotOutlined style={{ marginRight: 8 }} />
              {t('analysis.analyzing')}
            </div>
          </Card>
        )}

        {/* AI 分析结果 */}
        {result && result.ai_analysis && (
          <Card
            style={{
              marginBottom: 16,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 12
            }}
            bodyStyle={{ padding: 20 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <RobotOutlined style={{ fontSize: 20, marginRight: 8 }} />
              <Text strong style={{ color: 'white', fontSize: '16px' }}>
                {t('analysis.aiIntelligentAnalysis')}
              </Text>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              padding: 20,
              borderRadius: 8,
              color: 'white',
              lineHeight: 1.8,
              fontSize: '14px',
              maxHeight: '300px',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.3) transparent'
            }}>
              <div style={{
                whiteSpace: 'pre-line',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                {result.ai_analysis.analysis || t('analysis.noAiAnalysisResult')}
              </div>
            </div>
          </Card>
        )}

        {/* 分析结果内容 */}
        {result && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 24 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ marginTop: 20 }}>
                <div style={{ marginBottom: 16, textAlign: 'right' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveSignal}
                    disabled={!result || !onSaveSignal || !selectedPoint}
                    style={{
                      borderRadius: 8,
                      background: (!result || !onSaveSignal || !selectedPoint)
                        ? '#d9d9d9'
                        : 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                      border: 'none'
                    }}
                  >
                    {t('analysis.saveAsSignal')}
                  </Button>
                </div>
              <Tabs
                defaultActiveKey="1"
                style={{ marginTop: 8 }}
                onChange={setActiveInnerTab}
                destroyInactiveTabPane
                items={[
                  {
                    key: '1',
                    label: (
                      <Space>
                        <EyeOutlined />
                        {t('analysis.similarPointList')}
                      </Space>
                    ),
                    children: (
                      <SimilarPointList
                        filteredPoints={filteredPoints}
                        targetIndex={targetIndex}
                        displayCount={displayCount}
                        theme={theme}
                        t={t}
                        onViewPoint={(pt) => {
                          const tSec = toUnixSeconds(pt.datetime);
                          setActiveMiniChartPoint({ time: tSec, price: pt.price_data?.close });
                        }}
                        formatTimestamp={formatTimestamp}
                      />
                    )
                  },
                  {
                    key: '2',
                    label: (
                      <span>{t('analysis.subsequentReturnAnalysis')}</span>
                    ),
                    children: (
                      <SubsequentReturnAnalysisTab 
                        pointReturnsAnalysis={pointReturnsAnalysis}
                        discoveryData={result?.returns_analysis?.discovery}
                        validationData={result?.returns_analysis?.validation}
                        theme={theme}
                        t={t}
                      />
                    )
                  }
                ]}
              />
              </div>
            </div>
            <div style={{ minWidth: 520, marginTop: 111 }}>
              {miniChartData.length > 0 && (
                <MiniChart
                  data={miniChartData}
                  onClose={() => setActiveMiniChartPoint(null)}
                  title={miniChartTitle}
                  highlightRange={highlightRange}
                />
              )}
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default PointAnalysis; 